import React, { FC } from 'react';
import { useTranslation, Trans } from 'next-i18next';
import PitchPageLayout from 'src/common/components/PitchPageLayout';
import Typography from 'src/common/components/Typography';
import { HomepageListing } from 'src/common/types/api/v4/search/v1/homepage';
import { CarouselProvider } from 'src/common/providers/CarouselProvider';
import Carousel from 'src/common/components/dockyard/Carousel';
import ListingCard from 'src/common/components/ListingCard';
import CarouselButtons from 'src/common/components/dockyard/CarouselButtons';
import Button from 'src/common/components/ButtonDS22';
import { HOMEPAGE_INSTABOOK_GTAG_EVENT, PATHS } from 'src/common/constants';
import styles from './HomepageInstabookListings.module.scss';

type HomepageInstabookListingsProps = {
  listings: HomepageListing[];
};

const HomepageInstabookListings: FC<HomepageInstabookListingsProps> = ({ listings }) => {
  const [t] = useTranslation('home-page');

  return (
    <div className={styles.wrapper}>
      <PitchPageLayout backgroundColor="pearl">
        <div className={styles.root}>
          <CarouselProvider>
            <div className={styles.header}>
              <div className={styles.copyWrapper}>
                <div className={styles.title}>
                  <Typography
                    type="h2"
                    size="display-5"
                    weight="semibold"
                  >
                    {t('instabook-listings--title')}
                  </Typography>
                </div>
                <div className={styles.subtitle}>
                  <Typography
                    type="p"
                    size="body-xl"
                    weight="regular"
                  >
                    <Trans
                      t={t}
                      i18nKey="instabook-listings--subtitle"
                      components={{ br: <br className={styles.mobileOnly} /> }}
                    />
                  </Typography>
                </div>
              </div>
              <div className={styles.desktopButtons}>
                <CarouselButtons spacing="together" variant="primary-dark" />
              </div>
            </div>
            <Carousel>
              {listings.map((listing, index) => {
                if (!listing.instabook_enabled) return null;
                return (
                  <ListingCard
                    key={listing.id}
                    listingId={listing.id}
                    listingUrl={listing.listing_url}
                    headline={listing.headline}
                    breadcrumbs={listing.breadcrumbs}
                    category={listing.cat_loc_breadcrumb}
                    rating={
                        listing.review_average ? parseFloat(listing.review_average) : undefined
                      }
                    awards={listing.user_details.awards}
                    isSuperOwner={listing.user_details.is_super_owner}
                    instantBookingAvailable
                    searchPricing={listing.search_price_nested}
                    imageSrc={listing.photo_url ?? ''}
                    capacity={t('listings-capacity', { count: listing.capacity })}
                    snoozeStartDate={listing?.snooze_start_date ?? undefined}
                    snoozeEndDate={listing?.snooze_end_date ?? undefined}
                    variant="instabook"
                    gtagEvent={HOMEPAGE_INSTABOOK_GTAG_EVENT}
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                );
              })}
            </Carousel>
            <div className={styles.viewMore}>
              <Button
                type="link"
                href={`${PATHS.SEARCH}?page=1&instabooks=true`}
                variant="tertiary"
                size="large"
              >
                <Typography
                  size="body-l"
                  type="p"
                  weight="semibold"
                >
                  {t('view-more')}
                </Typography>
              </Button>
            </div>
          </CarouselProvider>
        </div>
      </PitchPageLayout>
    </div>
  );
};

export default HomepageInstabookListings;
